import { mergePreviewContext } from '@/document-types/application/mappers/mapPreviewContextDto';
import type { DocumentContext } from '@/document-render/domain/DocumentContext';
import { parseCustomerLocale } from '@/document-render/domain/CustomerLocale';
import { getMockDocumentContext } from '@/document-render/mocks/MOCK_DOCUMENT_CONTEXT';
import { getEmptyDocumentContext } from './emptyDocumentContext';
import { normalizeDocumentBranding } from './documentBranding';
import { coerceNumericVariables } from './coerceNumericVariables';

export interface NormalizeDocumentContextOptions {
  /**
   * Which defaults the raw/host bag is merged over (LLAMS-610 Part 5). `'sample'` (default,
   * today's behaviour) fills unspecified fields from invented sample data — right for the
   * template editor's own preview, where there is no real record. `'empty'` fills them with
   * structure-safe empty values instead, so a quote's unfilled field falls to a placeholder
   * rather than a sample value like a mock customer name.
   */
  defaults?: 'sample' | 'empty';
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPriceBookRowCollection = (value: unknown): value is Array<Record<string, unknown>> =>
  Array.isArray(value) && value.length > 0 && isPlainObject(value[0]);

function resolvePriceBookRows(
  raw: Record<string, unknown>,
  merged: Record<string, unknown>,
  defaults: DocumentContext
): DocumentContext['priceBook'] {
  const rawPriceBook = raw.priceBook;

  // Host/CRM always sends an explicit array (including empty) for live quote preview + PDF export.
  if (Array.isArray(rawPriceBook)) {
    if (rawPriceBook.length === 0) {
      return [];
    }

    if (isPlainObject(rawPriceBook[0])) {
      return rawPriceBook as Array<Record<string, unknown>>;
    }
  }

  // Document-type catalog variables expose priceBook as a scalar bag — not row data.
  if (isPlainObject(rawPriceBook)) {
    return defaults.priceBook;
  }

  if (isPriceBookRowCollection(merged.priceBook)) {
    return merged.priceBook;
  }

  return defaults.priceBook;
}

/**
 * Merges API/host preview context with safe defaults so renderers never read
 * undefined nested fields (e.g. customer.tags on sparse quote sample context).
 */
export function normalizeDocumentContext(rawInput: Record<string, unknown>, options?: NormalizeDocumentContextOptions): DocumentContext {
  const raw = coerceNumericVariables(rawInput);
  const defaults = options?.defaults === 'empty' ? getEmptyDocumentContext() : getMockDocumentContext();
  const merged = mergePreviewContext(defaults, raw);

  const rawCustomer = isPlainObject(raw.customer) ? raw.customer : null;
  const customerRecord: Record<string, unknown> = isPlainObject(merged.customer) ? merged.customer : {};

  let normalizedTags = defaults.customer.tags;
  if (rawCustomer) {
    const rawTags = rawCustomer.tags;
    normalizedTags = Array.isArray(rawTags) ? rawTags.filter((t): t is string => typeof t === 'string') : [];
  }

  const priceBook = resolvePriceBookRows(raw, merged as Record<string, unknown>, defaults);

  let brandingSource: unknown;
  if (Object.prototype.hasOwnProperty.call(raw, 'branding')) {
    brandingSource = raw.branding;
  } else if (merged.branding) {
    brandingSource = merged.branding;
  }

  const branding = brandingSource !== undefined ? normalizeDocumentBranding(brandingSource) : undefined;

  let customerLocaleSource: unknown;
  if (Object.prototype.hasOwnProperty.call(raw, 'customerLocale')) {
    customerLocaleSource = raw.customerLocale;
  } else if (merged.customerLocale) {
    customerLocaleSource = merged.customerLocale;
  }
  const customerLocale = parseCustomerLocale(customerLocaleSource);

  const normalized: DocumentContext = {
    ...merged,
    customer: {
      ...defaults.customer,
      ...customerRecord,
      tags: normalizedTags
    },
    priceBook
  };

  if (branding && Object.keys(branding).length > 0) {
    normalized.branding = branding;
  } else {
    delete normalized.branding;
  }

  if (customerLocale) {
    normalized.customerLocale = customerLocale;
  } else {
    delete normalized.customerLocale;
  }

  return normalized;
}
